import api from './client';

export type LiveDemoLanguage = 'fr' | 'en';

export interface CrmLiveDemoSession {
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number | null;
  questions: string[];
}

export interface CrmLiveDemo {
  id: string;
  url: string;
  dealId: string | null;
  dealName: string | null;
  language: LiveDemoLanguage;
  prospectName: string;
  companyName: string;
  createdByUserId: string;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
  sessions: CrmLiveDemoSession[];
}

export interface CrmLiveDemoInput {
  dealId?: string | null;
  language: LiveDemoLanguage;
  prospectName?: string;
  companyName?: string;
}

const membersEndpoint = '/admin/crm/live-demos';
const publicEndpoint = '/crm-live-demo/links';

export async function getCrmLiveDemos(dealId?: string) {
  const { data } = await api.get<CrmLiveDemo[]>(membersEndpoint, {
    params: dealId ? { dealId } : {},
  });
  return data;
}

export async function createCrmLiveDemo(payload: CrmLiveDemoInput) {
  const { data } = await api.post<CrmLiveDemo>(membersEndpoint, payload);
  return data;
}

export async function revokeCrmLiveDemo(id: string) {
  await api.delete(`${membersEndpoint}/${id}`);
}

/** The prospect's page: no sign-in, the link's token is the key. */
export interface LiveDemoInfo {
  language: LiveDemoLanguage;
  prospectName: string;
  companyName: string;
  ready: boolean;
}

export interface LiveDemoStart {
  sessionId: string;
  language: LiveDemoLanguage;
  signedUrl: string;
  enterUrl: string;
  portalOrigin: string;
  screens: Record<string, string>;
  dynamicVariables: Record<string, string>;
}

export interface LiveDemoEnd {
  sessionId: string;
  conversationId: string | null;
  durationSeconds: number;
}

export async function getLiveDemoInfo(token: string) {
  const { data } = await api.get<LiveDemoInfo>(
    `${publicEndpoint}/${encodeURIComponent(token)}`
  );
  return data;
}

export async function startLiveDemo(token: string) {
  const { data } = await api.post<LiveDemoStart>(
    `${publicEndpoint}/${encodeURIComponent(token)}/start`
  );
  return data;
}

/** `beacon` when the page is closing: the request must outlive the tab. */
export async function endLiveDemo(
  token: string,
  payload: LiveDemoEnd,
  beacon = false
) {
  const url = `/api/v1${publicEndpoint}/${encodeURIComponent(token)}/end`;
  if (beacon && navigator.sendBeacon) {
    navigator.sendBeacon(
      url,
      new Blob([JSON.stringify(payload)], { type: 'application/json' })
    );
    return;
  }
  await api.post(`${publicEndpoint}/${encodeURIComponent(token)}/end`, payload);
}
